import { useState, useEffect } from 'react';
import { 
  Lightbulb, 
  Clock, 
  CheckCircle, 
  XCircle, 
  ExternalLink,
  Mail,
  Building2,
  RefreshCw,
  Loader2,
  UserCheck,
  Filter,
  UserPlus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { suggestionsApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const statusConfig = {
  pending: { label: 'Pending', className: 'bg-warning/10 text-warning', icon: Clock },
  approved: { label: 'Approved', className: 'bg-success/10 text-success', icon: CheckCircle },
  rejected: { label: 'Rejected', className: 'bg-destructive/10 text-destructive', icon: XCircle },
};

export const SuggestionsManager = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'vendors', 'suggestions'

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await suggestionsApi.getAll();
      setSuggestions(data);
    } catch (err) {
      setError('Failed to load suggestions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const handleStatusUpdate = async (suggestionId, status, isVendorApp = false) => {
    setUpdating(suggestionId);
    try {
      const result = await suggestionsApi.updateStatus(suggestionId, status);
      setSuggestions(prev => 
        prev.map(s => 
          s.id === suggestionId ? { ...s, status } : s
        )
      );
      
      // Show success message for vendor approvals
      if (status === 'approved' && isVendorApp) {
        if (result.vendor_created) {
          toast.success('Vendor Account Created', {
            description: `Invite email ${result.invite_sent ? 'sent' : 'queued'}. Vendor visible in Vendors tab.`,
            duration: 5000
          });
        } else if (result.message) {
          toast.info('Vendor Exists', {
            description: result.message,
            duration: 4000
          });
        }
      } else if (status === 'approved') {
        toast.success('Suggestion approved');
      } else if (status === 'rejected') {
        toast.success('Suggestion rejected');
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const handleCreateVendor = async (suggestionId) => {
    setUpdating(suggestionId);
    try {
      const result = await suggestionsApi.createVendor(suggestionId);
      
      if (result.status === 'exists') {
        toast.info('Vendor Already Exists', {
          description: result.message,
          duration: 4000
        });
      } else if (result.status === 'created') {
        toast.success('Vendor Account Created', {
          description: `Invite email ${result.invite_sent ? 'sent' : 'queued'}. Vendor visible in Vendors tab.`,
          duration: 5000
        });
        // Update the suggestion in state to show as vendor application
        setSuggestions(prev => 
          prev.map(s => 
            s.id === suggestionId ? { ...s, status: 'approved', is_vendor_application: true } : s
          )
        );
      }
    } catch (err) {
      console.error('Failed to create vendor:', err);
      toast.error('Failed to create vendor');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button variant="outline" onClick={fetchSuggestions}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const pendingCount = suggestions.filter(s => s.status === 'pending').length;
  const vendorApplicationsCount = suggestions.filter(s => s.is_vendor_application).length;
  const pendingVendorCount = suggestions.filter(s => s.status === 'pending' && s.is_vendor_application).length;

  // Filter suggestions based on selected filter
  const filteredSuggestions = suggestions.filter(s => {
    if (filter === 'vendors') return s.is_vendor_application;
    if (filter === 'suggestions') return !s.is_vendor_application;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-primary" />
            Platform Suggestions &amp; Vendor Applications
          </h2>
          <p className="text-sm text-muted-foreground">
            Review user-submitted suggestions and vendor claim requests
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingVendorCount > 0 && (
            <Badge variant="secondary" className="bg-primary/10 text-primary">
              {pendingVendorCount} vendor {pendingVendorCount === 1 ? 'app' : 'apps'}
            </Badge>
          )}
          {pendingCount > 0 && (
            <Badge variant="secondary" className="bg-warning/10 text-warning">
              {pendingCount} pending
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={fetchSuggestions}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="all" className="gap-2">
            All ({suggestions.length})
          </TabsTrigger>
          <TabsTrigger value="vendors" className="gap-2">
            <UserCheck className="w-4 h-4" />
            Vendor Applications ({vendorApplicationsCount})
          </TabsTrigger>
          <TabsTrigger value="suggestions" className="gap-2">
            <Lightbulb className="w-4 h-4" />
            Suggestions ({suggestions.length - vendorApplicationsCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Suggestions List */}
      {filteredSuggestions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Lightbulb className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {filter === 'all' ? 'No suggestions yet' : 
               filter === 'vendors' ? 'No vendor applications yet' : 
               'No platform suggestions yet'}
            </p>
            <p className="text-sm text-muted-foreground">
              {filter === 'vendors' 
                ? 'Vendor applications appear when users apply to claim their platforms'
                : 'Users can submit suggestions via the footer link'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredSuggestions.map((suggestion) => {
            const status = statusConfig[suggestion.status] || statusConfig.pending;
            const StatusIcon = status.icon;
            const isVendor = suggestion.is_vendor_application;
            
            return (
              <Card 
                key={suggestion.id || suggestion.created_at}
                className={cn(isVendor && 'border-primary/30 bg-primary/5')}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">
                          {suggestion.platform_name}
                        </h3>
                        {isVendor && (
                          <Badge className="bg-primary/10 text-primary text-xs">
                            <UserCheck className="w-3 h-3 mr-1" />
                            Vendor Application
                          </Badge>
                        )}
                        <Badge className={cn('text-xs', status.className)}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </div>
                      
                      {/* Company & Website */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {suggestion.company_name}
                        </span>
                        {suggestion.website_url && (
                          <a 
                            href={suggestion.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 hover:text-foreground"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Website
                          </a>
                        )}
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {suggestion.contact_email}
                        </span>
                      </div>
                      
                      {/* Reason */}
                      <div className="bg-muted/50 rounded-lg p-3 mb-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          {isVendor ? 'Role / Authorization:' : 'Reason:'}
                        </p>
                        <p className="text-sm text-foreground">{suggestion.reason}</p>
                      </div>
                      
                      {/* Timestamp */}
                      <p className="text-xs text-muted-foreground">
                        Submitted: {new Date(suggestion.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    {suggestion.status === 'pending' && (
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-success hover:text-success"
                          disabled={updating === suggestion.id}
                          onClick={() => handleStatusUpdate(suggestion.id, 'approved', isVendor)}
                        >
                          {updating === suggestion.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : isVendor ? (
                            <UserPlus className="w-4 h-4 mr-1" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-1" />
                          )}
                          {isVendor ? 'Approve & Create Vendor' : 'Approve'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          disabled={updating === suggestion.id}
                          onClick={() => handleStatusUpdate(suggestion.id, 'rejected', isVendor)}
                        >
                          {updating === suggestion.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <XCircle className="w-4 h-4 mr-1" />
                          )}
                          Reject
                        </Button>
                        {/* Show Create Vendor button for non-vendor suggestions */}
                        {!isVendor && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-primary hover:text-primary"
                            disabled={updating === suggestion.id}
                            onClick={() => handleCreateVendor(suggestion.id)}
                          >
                            {updating === suggestion.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserPlus className="w-4 h-4 mr-1" />
                            )}
                            Create Vendor
                          </Button>
                        )}
                      </div>
                    )}
                    {/* Show Create Vendor button for already approved non-vendor suggestions */}
                    {suggestion.status === 'approved' && !isVendor && (
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-primary hover:text-primary"
                          disabled={updating === suggestion.id}
                          onClick={() => handleCreateVendor(suggestion.id)}
                        >
                          {updating === suggestion.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <UserPlus className="w-4 h-4 mr-1" />
                          )}
                          Create Vendor
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
